/**
 * THE RUNTIME RENDERER. PixiJS 8. Authors at 960x540, delivers by integer 2x nearest-neighbour.
 *
 * -- WHAT IT DRAWS, AND FROM WHAT ---------------------------------------------
 *
 *   the sea      OPEN WATER (CONTRACT 1.5g(a): POKOMOKO governs it). src/render/baked/water.png
 *                is the reference's sixteen-frame tile at the sea's scale - baked by
 *                tools/world-render/bake-water.mjs, every spec figure MEASURED on the output in
 *                water.json - laid over the world in 48 px tiles anchored to WORLD coordinates,
 *                each on the frame water.ts gives it for this instant. The base value is the
 *                clear colour; the tiles carry the mid, the crest and the highlight. There are
 *                no flecks: every bright pixel POKOMOKO has belongs to the crest line.
 *                ROUND 5 (CONTRACT 1.5i(c), (d)): the base is `seaBase(sea)`, a blend the owner
 *                tunes, and the tile is MULTIPLIED by the calm field of water.ts - full amplitude
 *                at the boat and at every shoreline, falling to a calm floor lifted by slow
 *                gusts on the open sea. The multiply is done by drawing each 16 px cell from one
 *                of 32 recoloured copies of the sheet (palette.ts `waterPalette`), so every cell
 *                is still four hard values and the tile itself is never resampled or blended.
 *   islands      baked sprites from src/render/baked/ - keyed, halo-stripped and sized in metres
 *                through stature's rule by tools/world-render/bake.mjs - each with the contact
 *                treatment from contact.ts drawn UNDER it on the water: the rim as opaque
 *                pixels, the shadow as a MULTIPLY over whatever the water shows there. That is
 *                what "multiplicative on value" means once the water moves; every shoreline
 *                number is Eastward's and unchanged.
 *   the boat     the one hull asset, bow-up, rotated on the CPU to the heading (rotate.ts) so
 *                its contact treatment and its mask come from the silhouette actually drawn.
 *
 * -- WHAT IT NEVER DOES --------------------------------------------------------
 *
 * It reads a frame description and draws it. It holds no clock, opens no session, and writes
 * no state; world-entry.ts computes the frame from `session.view(nowMs)` and calls `draw` only
 * when something in it changed. There is no ticker and no render loop in this file: with
 * `autoStart: false` and `sharedTicker: false` PixiJS renders exactly when `draw` is called.
 * The sea's loop advancing is a change in the frame's key like any other; between frames the
 * host sleeps. That is how the idle bar is met, and it is measured in the browser, not
 * asserted here.
 *
 * -- THE TWO PASSES ------------------------------------------------------------
 *
 * Pass 1 renders the scene into a RenderTexture the size of the ART VIEWPORT with nearest
 * sampling - 960x540 when pinned or on a 16:9 screen, however many art pixels the screen holds
 * otherwise (CONTRACT 1.5ac, render/fill.ts). Pass 2 draws that texture as one sprite scaled by
 * the whole-number grain into the canvas. Every screen pixel is therefore a copy of exactly one
 * authored pixel, which is the only legal way up (CONTRACT 1.5e). The canvas is sized in CSS so
 * that its backing store maps 1:1 to device pixels; a non-integer CSS scale would blur it and is
 * refused by construction.
 *
 * -- THE MATERIAL MASK ---------------------------------------------------------
 *
 * `setMaskMode(true)` draws the same frame as two flat colours: water white, not-water black.
 * It is the same geometry that painted the pixels - a pixel is water if and only if no sprite
 * covered it - which is what satisfiable.mjs criterion Q3 asks a frame to ship with. The water
 * tiles, the contact treatment and the chart are all off in the mask: a moving crest is water.
 */

import { Application, BufferImageSource, Container, Rectangle, RenderTexture, Sprite, Texture, TextureSource } from 'pixi.js';
import { BAND_PX, contactBand, type FoamSpec } from './contact';
import { paintOrder, spriteFootPx } from './depth';
import { hash32 } from './hash';
import { paintFishing, type FishingVisual } from './fishing';
import { MASK_LAND_RGB, MASK_WATER_RGB, SEA_BLEND_DEFAULT, CREST_LIFT_STEPS, WATER_RGBS, crestFor, foamFor, rgbHex, rimFor, seaBase, seaTone, waterPalette, type Rgb } from './palette';
import { rotateNearest, type RgbaImage } from './rotate';
import {
  SEA_CELL_PX,
  SEA_CREST_CELL_PX,
  bandOf,
  bandThreshold,
  bankIndexOf,
  buildCrestBank,
  buildEdgeBank,
  cellEdgeCrossings,
  crestDashAt,
  crestDashOnAt,
  crestCoverageAt,
  crestFieldAt,
  crestFrameKey,
  shorenessAt,
  marchCase,
  toneTWarped,
  warpInto,
} from './sea';
import { buildShelf, shelfDistAt, shelfProbeInto, shelfSolidSquare, type ShelfField } from './shelf';
import { composeBoatAppearance } from './boat-outfitting';
import { rootAssetUrls as bakedUrls, cosmeticAssetUrls } from './runtime-assets';
import { type Px } from './scale';
import { PINNED_VIEW, type ArtView } from './fill';
import {
  SHORE_CULL_MARGIN_PX,
  WATER_CALM,
  WATER_CELL_PX,
  WATER_FIELD,
  WATER_LEVELS,
  WATER_LOOP_FRAMES,
  WATER_MODE,
  WATER_SEA,
  WATER_TILE_PX,
  boxWithinFrame,
  calmAmplitude,
  crossWeightAt,
  heldFrame,
  levelOf,
  shelfCullMarginPx,
  tileFrame,
  tileRange,
  type CalmScene,
  type Shore,
  type WaterCalm,
  type WaterField,
  type WaterMode,
  type WaterSea,
} from './water';

export interface BakedSprite {
  readonly id: string;
  readonly file: string;
  readonly kind: string;
  /**
   * WHETHER THIS SILHOUETTE CAN CARRY A NAMED PLACE. Declared per sprite in
   * `tools/world-render/stature-islets.json` and carried through the bake, because it is a
   * judgement about the picture rather than about the id. `src/render/islets.ts` reads it;
   * optional here only so a manifest baked before the field existed still parses.
   */
  readonly harbour?: boolean;
  readonly spritePx: readonly [number, number];
  readonly anchor: readonly [number, number];
}

export interface BakedManifest {
  readonly note: string;
  readonly islets: readonly BakedSprite[];
  readonly boat: { readonly id: string; readonly file: string; readonly spritePx: readonly [number, number]; readonly longAxisPx: number };
}

/**
 * A HAND-AUTHORED ISLAND, from `baked/authored.json` (tools/island/bake-runtime.mjs).
 *
 * Kept in its own manifest rather than in `manifest.json`, because that file is regenerated
 * wholesale by tools/world-render/bake.mjs from diffusion samples and would drop anything drawn
 * by a person on its next run. `place` is the charted place this island IS: the render layer's
 * own mapping, so the voyage layer never carries art.
 */
export interface AuthoredIsland {
  readonly id: string;
  readonly name: string;
  readonly file: string;
  readonly place: string;
  readonly spritePx: readonly [number, number];
  readonly metres: readonly [number, number];
  /** THE ARRIVAL POINT, in sprite px: the water a boat lying at the place point sits on. */
  readonly anchor: readonly [number, number];
}

export interface AuthoredManifest {
  readonly note: string;
  readonly islands: readonly AuthoredIsland[];
}

/** An islet the entry wants drawn: its sprite id and its centre in WORLD art pixels. */
export interface PlacedIslet {
  readonly id: string;
  readonly sprite: string;
  readonly x: number;
  readonly y: number;
}

/** Everything one frame needs. Computed by the host from `session.view(nowMs)`; drawn here. */
export interface DrawFrame {
  /** A string that changes whenever the picture would: the host draws only on a change. */
  readonly key: string;
  readonly nowMs: number;
  /** Camera centre, in world art pixels, already rounded to integers. */
  readonly camPx: Px;
  /**
   * THE BOAT, in world art pixels, already rounded, and NO LONGER THE SAME POINT AS THE CAMERA.
   *
   * Until this field existed the two were the same by construction: the host set `camPx` to the
   * rounded boat position, this file drew the hull at the literal `ART_W / 2`, and the calm
   * field took its live centre from `camPx`. That gave the boat exactly 270 art px of frame on
   * every side of it forever, which is why seven of the twenty charted places cut their island
   * off at a frame edge when the boat was moored there and why 63.9% of Murrin Roost - the one
   * authored island, the one the owner ruled on - had never been drawn. See `interpolate.ts`,
   * "THE FRAME ON ARRIVAL", for the arithmetic and for the camera that answers it.
   *
   * Two things in this file read it and nothing else may. The hull is drawn HERE rather than at
   * the frame's centre, and the calm field's live disc is centred HERE rather than on the camera
   * - the water is alive round the boat and round a shore, and a camera that has swung onto an
   * island must not drag the boat's own patch of live water onto the land with it.
   *
   * In open water it is equal to `camPx`, exactly, so a frame with no island alongside is the
   * frame this renderer drew before any of it existed.
   */
  readonly boatPx: Px;
  readonly boatHeadingDeg: number;
  /** The open water's loop frame at `nowMs`, 0..15, from water.ts: a pure function of the instant. */
  readonly waterFrame: number;
  /**
   * The instant that wave frame began (water.ts `waterTickAt`): what the calm field is sampled
   * at, so a host that woke late inside the frame draws the identical field.
   */
  readonly waterTickMs: number;
  readonly islets: readonly PlacedIslet[];
}

export interface WorldViewStats {
  readonly draws: number;
  readonly boatRebuilds: number;
  readonly lastDrawMs: number;
  readonly grain: number;
  /** The art viewport the last draw covered (CONTRACT 1.5ac): 960x540 when pinned or on a 16:9 screen. */
  readonly viewW: number;
  readonly viewH: number;
  /** Water tiles covered by the last draw, and the cells (WATER_CELL_PX) actually placed. */
  readonly waterTiles: number;
  readonly waterCells: number;
  readonly waterField: WaterField;
  /**
   * WHICH SEA THIS FRAME IS (CONTRACT 1.5w). Every instrument in this round records it, because
   * a figure measured on one arm says nothing about the other and a JSON that does not name the
   * arm cannot be read a week later.
   */
  readonly waterMode: WaterMode;
  /** The render arm's constants as this view was built with them; all GUESSes, all `?`-tunable. */
  readonly seaParams: WaterSea;
  /** Render arm only: the sprites the tone sea actually drew this frame. 0 on the settled arm. */
  readonly toneRuns: number;
  readonly edgeCells: number;
  readonly crestCells: number;
  /**
   * Cells whose four corners span MORE than two bands, so the one boundary overlay a cell gets
   * cannot say the whole truth. Reported rather than hidden: if this climbs, either the tone
   * count or the cell size is wrong.
   */
  readonly mixedCells: number;
  /**
   * What the render arm's generated banks cost in texture memory, MEASURED off the buffers
   * themselves. 0 on the settled arm, which pays 4,718,592 B for its 32 recoloured sheets instead.
   */
  readonly bankBytes: number;
  /**
   * How often the frozen half of the field had to be recomputed, and the crest field with it.
   * A memo of a pure function keyed on all its inputs; see `drawToneSea`. If `fieldMisses` ever
   * equals `draws` on a still boat, the key has picked up something that changes every frame.
   */
  readonly fieldMisses: number;
  readonly crestMisses: number;
  /** Draws on which the crest's FROZEN half (dash gate, warped sample positions) had to be rebuilt. */
  readonly latticeMisses: number;
  /** Where drawToneSea's milliseconds go, for the reader of the cost table. */
  readonly probe?: { field: number; edges: number; crest: number; hide: number };
  /**
   * FOAM COLUMNS by material over every island loaded, [sand, lip, rock]. MEASURED and reported
   * rather than assumed, because the answer is small and it is the round's own finding: this
   * game's islands are rock to the waterline, so almost no shoreline in it asks for surf.
   */
  readonly foamColumns: readonly [number, number, number];
  readonly foamPx: number;
  /** The base blend and the calm the field was drawn with. */
  readonly sea: number;
  readonly seaRgb: Rgb;
  readonly calm: WaterCalm;
  /**
   * ISLANDS THAT HAVE ARRIVED, out of the number the manifests name. The first frame does not
   * wait for any of them, so this is below `spritesTotal` for as long as the art is still on
   * the wire and equal to it afterwards.
   *
   * `arrivalRedraws` is what that streaming COSTS in draws: one per batch of arrivals that
   * settle together, bounded by `spritesTotal`, and it stops moving for good once the last
   * sprite is in. If it is still climbing on a view that has finished loading, something has
   * put island loading inside the frame loop and this is where that shows up.
   */
  readonly spritesLoaded: number;
  readonly spritesTotal: number;
  readonly arrivalRedraws: number;
  /**
   * THE TWO NUMBERS THAT SAY THE FIRST FRAME DID NOT WAIT FOR THE ART, latched once each and
   * never moved again, so a reader gets the ordering that actually happened rather than
   * whatever the view has settled into by the time they look.
   *
   * `spritesAtFirstDraw` is `spritesLoaded` as the first frame was composed. Zero means the
   * player saw sea and boat with every island still on the wire. `drawsBeforeLastArrival` is
   * `draws` at the moment the last island registered - how many frames the player watched
   * before the world finished filling in.
   *
   * Both are -1 until the moment they describe has happened: -1 for `spritesAtFirstDraw`
   * means nothing has been drawn, -1 for `drawsBeforeLastArrival` means art is still in
   * flight (or an island failed to load and never will arrive).
   *
   * WHY THEY ARE HERE AND NOT INFERRED. `spritesLoaded` alone cannot answer this: read it a
   * second late on a fast connection and it is 18 whether the first frame waited or not.
   * Restoring the `await` over the island loads that this function deliberately does not do
   * changes no build output, no type and no test - it only changes these two numbers, to 18
   * and 0. `tools/lanes/platform-trust/delivered.test.ts` holds the page still with the
   * island responses on a barrier and reads them.
   */
  readonly spritesAtFirstDraw: number;
  readonly drawsBeforeLastArrival: number;
}

export interface WorldView {
  readonly canvas: HTMLCanvasElement;
  /** Device pixels per art pixel, as last set. A whole number, at least 1. */
  readonly grain: number;
  readonly manifest: BakedManifest;
  readonly authored: AuthoredManifest;
  /**
   * RE-FIT TO A BOX THAT CHANGED SIZE. The host measures - the renderer never does, so nothing
   * here reads the layout and nothing here runs unless the window actually moved.
   *
   * `grain` is device pixels per art pixel and must be a whole number; `dpr` is what the CSS
   * box is divided by. Both come from `fitToBox`. The authoring grid never changes: the world
   * is drawn into a 960x540 render texture either way and only the final blit gets bigger, so
   * this costs one renderer resize and one redraw, not a re-bake.
   */
  setGrain(grain: number, dpr: number, view?: ArtView, canvasDevice?: ArtView): void;
  /** The art viewport as last set: whole art columns and rows. */
  readonly view: ArtView;
  draw(frame: DrawFrame): void;
  /** Appearance only; the unchanged hull remains the water-contact and clearance authority. */
  setBoatFittings(ids: readonly string[]): Promise<void>;
  /** Two flat colours: water and not-water. Redraws the last frame. */
  setMaskMode(on: boolean): void;
  readonly maskMode: boolean;
  /**
   * THE CHART, and the catch mark on the water. Both are RGBA at the AUTHORING grid, so they
   * are doubled by the same integer 2 as the sea and never resampled. Passing null clears.
   *
   * The chart comes as two layers from chart.ts `toLayers`: the sheet itself, drawn normally,
   * and the shadow it casts on the water above its torn edge as MULTIPLY factors, so the cast
   * darkens the moving water rather than pasting a flat strip over it.
   *
   * They live in their own layer above everything and are excluded from two things on
   * purpose: the material mask, because a chart is not a material and satisfiable.mjs asks
   * "is this pixel water", and `capture`, because every instrument in this run judges the
   * WORLD and a 12% strip of paper across the bottom would move every figure it computes.
   */
  setChart(paper: RgbaImage | null, cast: RgbaImage | null, y: number, x?: number): void;
  /**
   * The ring on the water where a fish just came aboard, placed at an offset FROM THE BOAT in
   * screen art px, not from the frame's centre. Those were the same point until the camera was
   * allowed to swing onto an island on arrival; an offset from the centre would leave the mark
   * floating in open water a couple of hundred pixels off the hull that caught the fish. The
   * offset is held and re-applied on every draw, so the mark stays on the boat through a swing
   * that redraws far more often than the mark's own sixteen dither levels change.
   */
  setCatchMark(img: RgbaImage | null, dx: number, dy: number): void;
  /**
   * THE LINE ON THE WATER: the fishing visual (render/fishing.ts) painted as hard pixels and
   * placed off the BOAT, like the catch mark, so the arrival camera's swing carries the line
   * with the hull. Null clears it. It is drawn in a layer of its own on the water, BENEATH the
   * hull and every island, so it leaves the silhouette's edge rather than crossing the deck,
   * and it is off in the mask and out of `capture` for the reason the chart is: the
   * instruments judge the world, and a line is not a material.
   */
  setFishingVisual(model: FishingVisual | null): void;
  /**
   * Show or hide the GUI layer and redraw. For the delivered-screenshot check only: `capture`
   * already omits the chart from the authored frame, so a browser screenshot compared against
   * it has to omit the chart too, or verify-delivery.mjs is comparing two different pictures.
   */
  setGuiVisible(on: boolean): void;
  /** The last frame at the authoring grid, as PNG data URLs, in both modes. */
  capture(): Promise<{ frame: string; mask: string; key: string }>;
  stats(): WorldViewStats;
}

interface LoadedSprite {
  readonly id: string;
  readonly w: number;
  readonly h: number;
  /**
   * The row below the LOWEST OPAQUE ROW of this sprite: where the thing stands on the water.
   *
   * It is `h` for every baked island, because the bake crops each one to its own silhouette. It is
   * NOT `h` for the boat: the hull is rotated into a square big enough to hold it at any heading,
   * so the box bottom sits 5 to 7 px below the keel and using it would push the boat that much
   * nearer the camera than it is. Measured off the mask the sprite is actually drawn from.
   */
  readonly footPx: number;
  readonly anchor: Px;
  readonly texture: Texture;
  readonly maskTexture: Texture;
  readonly rimTexture: Texture;
  readonly shadowTexture: Texture;
  readonly bandH: number;
  /**
   * THE SHELF (CONTRACT 1.5w, `?water=render` only): how far the water at a point is from THIS
   * sprite's own silhouette, and what the land it is near is made of. Built once here from the
   * same bytes `contactBand` reads, null on the settled arm and null for the boat - a hull is
   * not a shore, and it is the one sprite in the frame that moves.
   */
  readonly shelf: ShelfField | null;
  /** The foam plane of the same contact band, null on the settled arm and null for the boat. */
  readonly foamTexture: Texture | null;
  readonly foamPx: number;
  readonly foamColumns: readonly [number, number, number];
}

interface ContactNode {
  readonly img: Sprite;
  readonly rim: Sprite;
  readonly shadow: Sprite;
  /** The foam plane, round 9. Hidden on the settled arm and on the boat, which has none. */
  readonly foam: Sprite;
}

interface IslandNode extends ContactNode {
  readonly loaded: LoadedSprite;
  lastSeen: number;
}

function urlFor(file: string): string {
  const url = bakedUrls[`./baked/${file}`];
  if (!url) throw new Error(`src/render/baked/${file} is not baked; run node tools/world-render/bake.mjs and bake-water.mjs`);
  return url;
}

async function loadRgba(url: string): Promise<RgbaImage> {
  const img = new Image();
  img.src = url;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2d context to read a baked sprite through');
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { w: canvas.width, h: canvas.height, rgba: data.data };
}

function maskOf(img: RgbaImage): Uint8Array {
  const mask = new Uint8Array(img.w * img.h);
  for (let q = 0; q < mask.length; q++) mask[q] = (img.rgba[q * 4 + 3] ?? 0) >= 128 ? 1 : 0;
  return mask;
}

function textureFromRgba(w: number, h: number, rgba: Uint8ClampedArray | Uint8Array): Texture {
  const bytes = rgba instanceof Uint8Array ? rgba : new Uint8Array(rgba.buffer, rgba.byteOffset, rgba.byteLength);
  const source = new BufferImageSource({ resource: bytes, width: w, height: h, scaleMode: 'nearest' });
  return new Texture({ source });
}

/** The silhouette as opaque MASK_LAND pixels: what the material mask paints for this sprite. */
function silhouetteRgba(mask: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h * 4);
  for (let q = 0; q < mask.length; q++) {
    if (!mask[q]) continue;
    out[q * 4] = MASK_LAND_RGB[0];
    out[q * 4 + 1] = MASK_LAND_RGB[1];
    out[q * 4 + 2] = MASK_LAND_RGB[2];
    out[q * 4 + 3] = 255;
  }
  return out;
}

function buildLoaded(id: string, img: RgbaImage, anchor: Px, rim: Rgb, contactImage: RgbaImage = img, shelfPx: number | null = null, foam: FoamSpec | null = null): LoadedSprite {
  const mask = maskOf(img);
  const contactMask = contactImage === img ? mask : maskOf(contactImage);
  const band = contactBand(contactMask, img.w, img.h, rim, foam);
  return {
    shelf: shelfPx === null ? null : buildShelf(mask, img.rgba, img.w, img.h, shelfPx),
    foamTexture: band.foam === null ? null : textureFromRgba(band.w, band.h, band.foam),
    foamPx: band.foamPx,
    foamColumns: band.foamColumns,
    id,
    w: img.w,
    h: img.h,
    footPx: spriteFootPx(contactMask, img.w, img.h),
    anchor,
    texture: textureFromRgba(img.w, img.h, img.rgba),
    maskTexture: textureFromRgba(img.w, img.h, silhouetteRgba(mask, img.w, img.h)),
    rimTexture: textureFromRgba(band.w, band.h, band.rim),
    shadowTexture: textureFromRgba(band.w, band.h, band.shadow),
    bandH: band.h,
  };
}

function destroyLoaded(l: LoadedSprite): void {
  l.texture.destroy(true);
  l.maskTexture.destroy(true);
  l.rimTexture.destroy(true);
  l.shadowTexture.destroy(true);
  l.foamTexture?.destroy(true);
}

/** A sprite, a rim sprite and a MULTIPLY shadow sprite: the three parts of a thing on the water. */
function contactNode(): ContactNode {
  const shadow = new Sprite();
  shadow.blendMode = 'multiply';
  return { img: new Sprite(), rim: new Sprite(), shadow, foam: new Sprite() };
}

/**
 * The baked sheet as CLASSES rather than colours: 0 base, 1 mid, 2 crest, 3 highlight, one
 * byte per pixel of the 768x48 sheet. The sheet's size is checked against water.ts so a stale
 * bake cannot be drawn as if it were current, and every pixel must be one of the four values.
 */
function classifyWaterSheet(sheet: RgbaImage): Uint8Array {
  const T = WATER_TILE_PX;
  if (sheet.w !== T * WATER_LOOP_FRAMES || sheet.h !== T) {
    throw new Error(`baked/water.png is ${sheet.w}x${sheet.h}; water.ts expects ${T * WATER_LOOP_FRAMES}x${T}. Run node tools/world-render/bake-water.mjs`);
  }
  const key = (r: number, g: number, b: number) => (r << 16) | (g << 8) | b;
  const classOf = new Map(WATER_RGBS.map((c, i) => [key(c[0], c[1], c[2]), i]));
  const classes = new Uint8Array(sheet.w * sheet.h);
  for (let q = 0; q < classes.length; q++) {
    const c = classOf.get(key(sheet.rgba[q * 4]!, sheet.rgba[q * 4 + 1]!, sheet.rgba[q * 4 + 2]!));
    if (c === undefined) throw new Error(`baked/water.png pixel ${q} is not one of the four water values`);
    classes[q] = c;
  }
  return classes;
}

/** The cells of one tile: a tile is CELLS x CELLS of them, in reading order. */
const CELLS = WATER_TILE_PX / WATER_CELL_PX;

/**
 * THE MULTIPLIED TILE, as textures. One recoloured copy of the whole sheet per amplitude
 * level - palette.ts `waterPalette(base, level / (LEVELS - 1))`, so level 0 is flat base and
 * the top level on the navy is POKOMOKO's four values verbatim - and, on each copy, one
 * texture per (frame, cell) that is a CELL x CELL window into it. `levels[k][f][cell]`. The
 * sprites in the pool point at these; nothing is uploaded after this returns.
 */
function buildWaterLevels(classes: Uint8Array, base: Rgb): Texture[][][] {
  const T = WATER_TILE_PX;
  const W = T * WATER_LOOP_FRAMES;
  const levels: Texture[][][] = [];
  for (let k = 0; k < WATER_LEVELS; k++) {
    const pal = waterPalette(base, k / (WATER_LEVELS - 1));
    const rgba = new Uint8Array(W * T * 4);
    for (let q = 0; q < classes.length; q++) {
      const c = pal[classes[q]!]!;
      rgba[q * 4] = c[0];
      rgba[q * 4 + 1] = c[1];
      rgba[q * 4 + 2] = c[2];
      rgba[q * 4 + 3] = 255;
    }
    const source = new BufferImageSource({ resource: rgba, width: W, height: T, scaleMode: 'nearest' });
    const frames: Texture[][] = [];
    for (let f = 0; f < WATER_LOOP_FRAMES; f++) {
      const cells: Texture[] = [];
      for (let cy = 0; cy < CELLS; cy++) {
        for (let cx = 0; cx < CELLS; cx++) {
          cells.push(new Texture({ source, frame: new Rectangle(f * T + cx * WATER_CELL_PX, cy * WATER_CELL_PX, WATER_CELL_PX, WATER_CELL_PX) }));
        }
      }
      frames.push(cells);
    }
    levels.push(frames);
  }
  return levels;
}

/**
 * A grain is DEVICE PIXELS PER ART PIXEL and there is no such thing as a fractional one: a 1.5x
 * nearest-neighbour blit gives some art pixels two device rows and some one, which is what
 * CONTRACT 1.5e means by non-integer upscaling destroying pixel art. Anything that is not a
 * whole number at least 1 is refused HERE rather than drawn, so a bad caller cannot ship a
 * resampled frame. `fitToBox` already returns whole numbers; this is the second lock.
 */
function wholeGrain(n: number): number {
  if (!Number.isInteger(n) || n < 1) {
    throw new Error(`world-view: grain must be a whole number of device pixels per art pixel, at least 1; got ${n}`);
  }
  return n;
}

/** An art viewport must be whole art px, at least one each way; the second lock after `fitFill`. */
function wholeView(v: ArtView): ArtView {
  if (!Number.isInteger(v.w) || !Number.isInteger(v.h) || v.w < 1 || v.h < 1) {
    throw new Error(`world-view: the art viewport must be whole art pixels; got ${v.w}x${v.h}`);
  }
  return v;
}

export interface CreateWorldViewOptions {
  readonly host: HTMLElement;
  /**
   * DEVICE pixels per art pixel, and it comes from `fitToBox` reading the HOST ELEMENT. 2 is
   * the ruled delivery (1920x1080, CONTRACT 1.5e); 1 fits a 960x540 box; 3 and up fill a
   * bigger one. `setGrain` moves it when the window does, so this is only the first value.
   */
  readonly grain: number;
  /**
   * THE ART VIEWPORT (CONTRACT 1.5ac): how many art columns and rows the frame shows. The authored
   * grid is still 960x540 and nothing is ever scaled by a fraction; a screen that is not 16:9 simply
   * sees more or fewer art pixels at the same whole grain. Defaults to the pinned 960x540.
   */
  readonly view?: ArtView;
  /** How the open-water tiles are phased across the field. Defaults to water.ts's ruling. */
  readonly waterField?: WaterField;
  /** The base blend, 0 navy .. 1 Eastward (palette.ts `seaBase`). Defaults to SEA_BLEND_DEFAULT. */
  readonly sea?: number;
  /** Overrides on water.ts WATER_CALM, each a GUESS the owner tunes by eye. */
  readonly calm?: Partial<WaterCalm>;
  /**
   * WHICH SEA (CONTRACT 1.5w, `?water=`). 'render' is the tone sea of round 9; 'settled' is the
   * sea this game shipped on, unmoved. ONE arm's textures are ever built - the settled arm's 32
   * recoloured copies of the sheet cost a MEASURED 4,718,592 B and the render arm never pays it.
   */
  readonly waterMode?: WaterMode;
  /** Overrides on water.ts WATER_SEA. Read only by the render arm. */
  readonly seaParams?: Partial<WaterSea>;
}

export async function createWorldView(options: CreateWorldViewOptions): Promise<WorldView> {
  let grain = wholeGrain(options.grain);
  let viewW = wholeView(options.view ?? PINNED_VIEW).w;
  let viewH = wholeView(options.view ?? PINNED_VIEW).h;
  /**
   * THE CAMERA'S SCREEN CENTRE, in whole art px. `floor(w / 2)` so an odd view still puts the camera
   * on a pixel; at 960x540 it is 480, 270 - the literal `ART_W / 2, ART_H / 2` this file drew at
   * before the view could vary, so a pinned frame is the same arithmetic to the pixel.
   */
  let halfW = Math.floor(viewW / 2);
  let halfH = Math.floor(viewH / 2);
  const waterField: WaterField = options.waterField ?? WATER_FIELD;
  const sea = Math.min(1, Math.max(0, options.sea ?? SEA_BLEND_DEFAULT));
  const base = seaBase(sea);
  const rim = rimFor(base);
  const calm: WaterCalm = { ...WATER_CALM, ...options.calm };
  const waterMode: WaterMode = options.waterMode ?? WATER_MODE;
  const seaParams: WaterSea = { ...WATER_SEA, ...options.seaParams };
  /** How far off screen a SHELF may sit and still reach a pixel: its own reach plus the warp. */
  const shelfMargin = shelfCullMarginPx(seaParams);
  // Every texture this renderer ever creates samples nearest. Set before any is created.
  TextureSource.defaultOptions.scaleMode = 'nearest';

  const app = new Application();
  await app.init({
    width: viewW * grain,
    height: viewH * grain,
    resolution: 1,
    autoDensity: false,
    antialias: false,
    autoStart: false,
    sharedTicker: false,
    background: 0x000000,
    preference: 'webgl',
    roundPixels: true,
  });
  app.ticker.stop();
  const canvas = app.canvas as HTMLCanvasElement;
  canvas.style.imageRendering = 'pixelated';
  canvas.style.display = 'block';
  /**
   * THE ONE PLACE THE CANVAS IS SIZED, and it is called again every time the window moves.
   *
   * Two sizes, and keeping them apart is the whole of pixel purity. The BACKING STORE is
   * `ART * grain` DEVICE pixels, so one art pixel is a whole number of device pixels and the
   * nearest-neighbour blit lands on the device grid. The CSS box is that divided by dpr, which
   * is what puts one canvas pixel on one device pixel. Change grain and both move together.
   */
  function sizeCanvas(next: number, dpr: number, nextView: ArtView, canvasDevice?: ArtView): void {
    grain = wholeGrain(next);
    const v = wholeView(nextView);
    viewW = v.w;
    viewH = v.h;
    halfW = Math.floor(viewW / 2);
    halfH = Math.floor(viewH / 2);
    // Pass 1's target follows the view, and the caller redraws before the browser paints.
    //
    // THE SPRITE DOES NOT HEAR A RESIZE. MEASURED by full-screen.test.ts's purity probe: after a
    // 960x517 -> 960x540 resize the delivered canvas had 111,047 of 518,400 blocks impure while
    // `capture()` of the same texture was byte-identical to a page that never resized. Pixi 8's
    // Sprite subscribes to texture updates only for a `dynamic` texture, so it kept the old size
    // and stretched the new frame across it. Detaching and reattaching the texture is the
    // documented way to make it re-read the frame.
    if (art.width !== viewW || art.height !== viewH) {
      art.resize(viewW, viewH);
      screen.texture = Texture.EMPTY;
      screen.texture = art;
    }
    // THE BACKING STORE IS THE PICTURE, OR THE PICTURE GROWN TO A WHOLE CSS PX (fill.ts
    // `wholeCssCanvas`, and the measurement there). Never smaller than the picture and never a
    // whole art pixel larger: the blit below is still `grain` at the origin, so every art pixel is
    // the same `grain x grain` square of device pixels either way, and the remainder is the clear.
    const pw = viewW * grain;
    const ph = viewH * grain;
    const w = Math.max(pw, Math.min(pw + grain - 1, canvasDevice?.w ?? pw));
    const h = Math.max(ph, Math.min(ph + grain - 1, canvasDevice?.h ?? ph));
    if (canvas.width !== w || canvas.height !== h) app.renderer.resize(w, h);
    canvas.style.width = `${w / dpr}px`;
    canvas.style.height = `${h / dpr}px`;
    screen.scale.set(grain);
    if (!Number.isInteger(w / dpr)) {
      // Not fatal and not a purity failure: the BACKING store is still a whole multiple, so the
      // blit is still nearest-neighbour on whole device pixels. It only means the element's CSS
      // box lands on a fraction, which some compositors round.
      console.warn(`world-view: ${w} device px / dpr ${dpr} is ${w / dpr} CSS px, not a whole number; the element box may be rounded by the compositor.`);
    }
  }
  options.host.appendChild(canvas);

  const manifestUrl = new URL('./baked/manifest.json', import.meta.url).href;
  const manifest = (await (await fetch(manifestUrl)).json()) as BakedManifest;
  const authoredUrl = new URL('./baked/authored.json', import.meta.url).href;
  const authored = (await (await fetch(authoredUrl)).json()) as AuthoredManifest;

  const loaded = new Map<string, LoadedSprite>();

  /**
   * THE FIRST FRAME USED TO WAIT FOR EVERY ISLAND IN THE WORLD.
   *
   * This was one `Promise.all` over the islets, the authored islands, the boat and the water,
   * and nothing was drawn until the last byte of the last one had arrived and been decoded.
   * Measured on the built artefact before this change: 4,251,580 bytes over 30 requests, of
   * which 4,095,365 were PNG, and on a throttled slow-4G profile the last byte landed at 28.8
   * seconds. For that whole time the page was blank. The island the player is looking at was
   * not the reason - it was the seventeen islets scattered across a sea the camera cannot see
   * yet, all of them in front of the sea itself.
   *
   * So the wait is split by what the first frame actually needs:
   *
   *   BLOCKING - the water sheet and the boat. There is no frame without them: the water is
   *   every pixel behind everything, and the boat is the player. Together they are 12,705
   *   bytes of the 817,095 this build ships.
   *
   *   NOT BLOCKING - every island. `render` already skips a sprite that is not in `loaded`,
   *   in both places it looks one up, because a place can name a sprite the manifest does not
   *   carry. That tolerance was written for a different reason and it is exactly what lets an
   *   island arrive late. Each one paints itself in when it lands.
   *
   * The requests are issued HERE, before the WebGL context is built and the scene assembled,
   * so the network is busy during the setup that used to follow it. Only the completion
   * handler is attached at the bottom of this function, where `render` and `lastFrame` exist.
   *
   * WHY THIS AND NOT A SMALLER PLATE. Re-encoding took the baked set from 4,095,365 bytes to
   * 817,095 without touching a pixel, and that is worth having, but it does not survive the
   * art. CONTRACT 1.2d rules that a place is a whole island rendered on the card at 2048x512;
   * `tools/spritegen/out/island-whole/isleVillage2048-4000.png` is one, it weighs 783,850
   * bytes, it holds 74,996 distinct colours, and re-encoding it through the fixed writer makes
   * it BIGGER, not smaller - it is already at its floor. There are twenty charted places. The
   * art this renderer is being built for is on the order of 16 MB, and no encoder setting
   * touches that number. Not blocking on it is the only thing that does.
   */
  // ISSUE ORDER IS THE WHOLE POINT, so the two blocking requests are started first and the
  // islands are started immediately behind them, before either is awaited.
  //
  // MEASURED, and it is why this is not one list: with the islands issued first, the boat came
  // back as request 31 of 32 on a cold load - the browser holds six connections open and the
  // one asset the first frame could not do without sat at the back of a queue of eighteen it
  // did not need. Nothing was blocked on the ISLAND bytes any more and the first frame was
  // still waiting for most of them. Issued this way the boat is request 2.
  const boatLoad = loadRgba(urlFor(manifest.boat.file));
  const waterLoad = loadRgba(urlFor('water.png'));

  const arriving = [
    ...manifest.islets.map((s) => ({ id: s.id, file: s.file, anchor: { x: s.anchor[0], y: s.anchor[1] } })),
    ...authored.islands.map((s) => ({ id: s.id, file: s.file, anchor: { x: s.anchor[0], y: s.anchor[1] } })),
  ].map((s) => ({
    spec: s,
    // `urlFor` still throws synchronously for a sprite that was never baked, so a missing bake
    // is still a hard boot failure. What becomes survivable is a FETCH or a DECODE failing,
    // and it should be: one island that will not load is one island missing from the sea, not
    // a blank page. Settled rather than rejected so nothing here is ever an unhandled rejection
    // in the gap between issuing the request and attaching the handler below.
    load: loadRgba(urlFor(s.file)).then(
      (img) => ({ ok: true as const, img }),
      (err: unknown) => ({ ok: false as const, err }),
    ),
  }));

  // Declared HERE, above the scene, because `render` reads `spritesLoaded` and is defined
  // below this point: every one of its call sites is further down still, but a counter the
  // renderer reads should not sit in a temporal dead zone that only stays harmless by
  // accident. The handlers that move them are at the bottom, where `render` exists.
  let spritesLoaded = 0;
  const foamColumns: [number, number, number] = [0, 0, 0];
  let foamPxTotal = 0;
  let arrivalRedrawQueued = false;
  let arrivalRedraws = 0;
  /** Latched once each; see `WorldViewStats`. -1 means the moment has not happened yet. */
  let spritesAtFirstDraw = -1;
  let drawsBeforeLastArrival = -1;

  const boatImage = await boatLoad;
  let boatAppearance = boatImage;
  let fittingKey: string | null = '';
  let fittingRequest = 0;
  const fittingLoads = new Map<string, Promise<RgbaImage>>();
  // ONE ARM'S TEXTURES. `water.png` is still blocking art and still loaded (BLOCKING_ART is
  // unchanged, and `?water=` must be one RELOAD apart, not one rebuild), but the 32 recoloured
  // copies of the sheet are only built for the arm that draws them.
  const waterSheet = await waterLoad;
  const waterLevels = waterMode === 'settled' ? buildWaterLevels(classifyWaterSheet(waterSheet), base) : [];

  // ---- scene ------------------------------------------------------------------------------
  const art = RenderTexture.create({ width: viewW, height: viewH, scaleMode: 'nearest' });
  const world = new Container();
  const waterLayer = new Container();
  const bandsLayer = new Container();
  // The fishing line's own layer: on the water, above the contact bands, BENEATH everything
  // that stands on the water. The line starts under the hull and shows from the silhouette's
  // edge at any heading; it runs under an island, not over the land. (Fix round 1: it sat in
  // the GUI layer over the boat, and the reveal's line was painted across the stern.)
  const lineLayer = new Container();
  const spritesLayer = new Container();
  const guiLayer = new Container();
  world.addChild(waterLayer, bandsLayer, lineLayer, spritesLayer, guiLayer);
  const screen = new Sprite(art);
  sizeCanvas(grain, window.devicePixelRatio || 1, { w: viewW, h: viewH });

  // The water: a pool of sprites, one per visible CELL, re-pointed at the right level, frame
  // and cell window on every draw. 21 x 13 tiles of 3 x 3 cells covers 960x540 at any camera;
  // nothing is created after warm-up. The second pool is the 'cross' field's upper layer.
  const waterPool: Sprite[] = [];
  const crossPool: Sprite[] = [];
  const crossLayer = new Container();
  waterLayer.addChild(crossLayer);
  let waterTiles = 0;
  let waterCells = 0;

  /**
   * THE TONE SEA'S THREE LAYERS (CONTRACT 1.5w), and they are PARENTED TO `waterLayer` on
   * purpose: mask-mode already hides that container and `capture()` already excludes nothing
   * from it, so the new sea inherits both for free and the pinned `world.addChild(...)` scan
   * that world-view.test.ts reads stays a five-child line.
   *
   *   toneLayer   the flat tones, drawn as HORIZONTAL RUNS of equal band: one tinted 1x1 white
   *               sprite per run, and an 8-bit multiply of white by a tint IS the tint exactly.
   *   edgeLayer   one hard-alpha marching-squares overlay per cell whose corners straddle a
   *               band edge, tinted to the upper band.
   *   crestLayer  the crest, which is a SECOND marching-squares band and not stamped sprites.
   */
  const toneLayer = new Container();
  const edgeLayer = new Container();
  const crestLayer = new Container();
  waterLayer.addChild(toneLayer, edgeLayer, crestLayer);
  /**
   * ONE POOL PER COLOUR, and it is a cost decision with a measurement behind it. Pixi 8's tint
   * setter runs `Color.shared.setValue(...)` on EVERY assignment, changed or not, and the tone
   * sea sets about 1,500 tints a draw. Bucketing the sprites by the colour they will be drawn in
   * - a container per tone band, and per (band, amplitude step) for the crest - means a sprite's
   * tint is set ONCE, when it is first created, and never again for the life of the view.
   *
   * The buckets are added to their layer in INCREASING BAND ORDER, which is also the order the
   * boundary overlays must be drawn in: a cell that spans three bands emits one overlay per level
   * and the higher one has to land on top.
   */
  interface Bucket { readonly container: Container; readonly pool: Sprite[]; used: number }
  function fillBuckets(layer: Container, into: Bucket[], n: number): void {
    for (let i = 0; i < n; i++) {
      const container = new Container();
      layer.addChild(container);
      into.push({ container, pool: [], used: 0 });
    }
  }
  function takeFrom(b: Bucket, tint: number, texture: Texture | null): Sprite {
    let s = b.pool[b.used];
    if (!s) {
      s = new Sprite(texture ?? Texture.WHITE);
      s.tint = tint;
      b.pool.push(s);
      b.container.addChild(s);
    } else if (texture !== null && s.texture !== texture) {
      s.texture = texture;
    }
    s.visible = true;
    b.used++;
    return s;
  }
  const toneBuckets: Bucket[] = [];
  const edgeBuckets: Bucket[] = [];
  const crestBuckets: Bucket[] = [];
  let toneRuns = 0;
  let edgeCells = 0;
  let crestCells = 0;
  let mixedCells = 0;

  /**
   * THE TONE RAMP as hex, once. `seaTone(base, 0)` IS the base, so tone 0 is exactly the clear
   * colour and `?sea=` keeps the meaning 1.5i(c) gave it.
   */
  const toneHex: number[] = [];
  for (let i = 0; i < seaParams.tones; i++) toneHex.push(rgbHex(seaTone(base, i / (seaParams.tones - 1))));

  /**
   * THE BANKS, generated from code at startup: zero new art, BLOCKING_ART unchanged, critical
   * path +0 bytes. Each is ONE BufferImageSource with a Texture window per tile - the pattern
   * `buildWaterLevels` already uses, and the reason is the same: 704 separate sources would
   * break Pixi's batcher at about sixteen textures and turn one draw call into a call storm.
   */
  const edgeBank: Texture[] = [];
  const crestBank: Texture[] = [];
  let edgeBankBytes = 0;
  let crestBankBytes = 0;
  if (waterMode === 'render') {
    const eb = buildEdgeBank(SEA_CELL_PX);
    const cb = buildCrestBank(SEA_CREST_CELL_PX, seaParams.crestStrokePx);
    edgeBankBytes = eb.rgba.length;
    crestBankBytes = cb.rgba.length;
    for (const [bank, out] of [[eb, edgeBank], [cb, crestBank]] as const) {
      const source = new BufferImageSource({ resource: bank.rgba, width: bank.w, height: bank.h, scaleMode: 'nearest' });
      for (let i = 0; i < bank.count; i++) {
        const tx = (i % bank.cols) * bank.cell;
        const ty = Math.floor(i / bank.cols) * bank.cell;
        out.push(new Texture({ source, frame: new Rectangle(tx, ty, bank.cell, bank.cell) }));
      }
    }
  }

  /**
   * THE PER-DRAW SCRATCH. Recomputed every draw, never read across one: it exists so a lattice
   * corner is sampled once instead of by each of the four cells that share it, and for no other
   * reason. Grown, never shrunk, and never anything but a function of this draw's own inputs.
   */
  let toneT_ = new Float32Array(0);
  let bandG = new Int16Array(0);
  let warpX = new Float32Array(0);
  let warpY = new Float32Array(0);
  let ampG = new Float32Array(0);
  let crestG = new Float32Array(0);
  let crestBase = new Float32Array(0);
  let crestNeed = new Uint8Array(0);
  let crestDash = new Uint8Array(0);
  /** The warped sample position of each crest corner. Frozen in world space; see the memo below. */
  let crestPX = new Float32Array(0);
  let crestPY = new Float32Array(0);
  /**
   * THE SHORE AT EACH CREST CORNER (CONTRACT 1.5y): the shelf distance, the inward unit normal
   * and the shoreness that follows from the distance. All three are FROZEN IN WORLD SPACE - the
   * shelf field is built once per sprite in `buildLoaded` and the warped sample position above is
   * already frozen - so they live in the lattice memo and cost nothing on a draw that hits it.
   */
  let crestD = new Float32Array(0);
  let crestNX = new Float32Array(0);
  let crestNY = new Float32Array(0);
  let crestSh = new Float32Array(0);
  /** Scratch for `shelfProbeInto`: distance, nx, ny. Not state; overwritten at every corner. */
  const shelfProbe = new Float32Array(3);
  /** The memo's keys, and how often it missed. See the note in `drawToneSea`. */
  let fieldCacheKey = '';
  let crestLatticeKey = '';
  /** The crest field memo's key: `crestFrameKey(waterFrame)`. NaN is "invalid", which no key equals. */
  let crestCacheFrame = Number.NaN;
  let latticeMisses = 0;
  let fieldMisses = 0;
  let crestMisses = 0;
  /** Two scratch buffers so the hot loops allocate nothing at all: 2,135 corners a draw. */
  let probe0 = 0; let probeA = 0; let probeB = 0; let probeC = 0; let probeD = 0;
  const warpPair = new Float64Array(2);
  const cross = new Int32Array(4);

  /**
   * THE CREST COLOURS, once: one per (tone band, quantised amplitude step). `crestFor` quantises
   * the amplitude to CREST_LIFT_STEPS, so this table is the whole set of crest values the frame
   * can hold - a small set of hard values, which is what 1.5e asks of a pixel-art surface.
   */
  const crestHex: number[] = [];
  for (let b = 0; b < seaParams.tones; b++) {
    for (let k = 0; k < CREST_LIFT_STEPS; k++) {
      crestHex.push(rgbHex(crestFor(seaTone(base, b / (seaParams.tones - 1)), k / (CREST_LIFT_STEPS - 1), seaParams.crestLift)));
    }
  }
  if (waterMode === 'render') {
    fillBuckets(toneLayer, toneBuckets, seaParams.tones);
    fillBuckets(edgeLayer, edgeBuckets, seaParams.tones);
    fillBuckets(crestLayer, crestBuckets, crestHex.length);
  }


  const islands = new Map<string, IslandNode>();
  let boat: { loaded: LoadedSprite; headingDeg: number } | null = null;
  const boatNode = contactNode();

  // The chart and the catch mark. Their textures are rebuilt only when the host hands over
  // a different image, which it does only when the copy or the dissolve step changed.
  const chartSprite = new Sprite();
  const chartCastSprite = new Sprite();
  chartCastSprite.blendMode = 'multiply';
  const catchSprite = new Sprite();
  /** Screen art px from the boat's own centre to the mark's top-left. Held, not baked in. */
  const catchOffset = { dx: 0, dy: 0 };
  // The fishing line, bobber and bite ring: one sprite off the boat, in the line layer.
  const fishingSprite = new Sprite();
  const fishingOffset = { dx: 0, dy: 0 };
  let fishingKey = '';
  chartSprite.visible = false;
  chartCastSprite.visible = false;
  catchSprite.visible = false;
  fishingSprite.visible = false;
  lineLayer.addChild(fishingSprite);
  guiLayer.addChild(chartCastSprite, chartSprite, catchSprite);
  let chartTexture: Texture | null = null;
  let chartCastTexture: Texture | null = null;
  let catchTexture: Texture | null = null;
  let fishingTexture: Texture | null = null;
  let guiVisible = true;

  let maskMode = false;
  let lastFrame: DrawFrame | null = null;
  let draws = 0;
  let boatRebuilds = 0;
  let lastDrawMs = 0;
  let drawSerial = 0;

  function boatFor(headingDeg: number): LoadedSprite {
    if (boat && boat.headingDeg === headingDeg) return boat.loaded;
    if (boat) destroyLoaded(boat.loaded);
    const rotated = rotateNearest(boatAppearance, headingDeg);
    const contactHull = boatAppearance === boatImage ? rotated : rotateNearest(boatImage, headingDeg);
    const l = buildLoaded(manifest.boat.id, rotated, { x: rotated.w / 2, y: rotated.h / 2 }, rim, contactHull);
    boat = { loaded: l, headingDeg };
    boatRebuilds++;
    return l;
  }

  function place(node: ContactNode, l: LoadedSprite, sx: number, sy: number): void {
    node.img.texture = maskMode ? l.maskTexture : l.texture;
    node.img.position.set(sx, sy);
    node.rim.texture = l.rimTexture;
    node.rim.position.set(sx, sy);
    node.rim.visible = !maskMode;
    node.shadow.texture = l.shadowTexture;
    node.shadow.position.set(sx, sy);
    node.shadow.visible = !maskMode;
    // The foam sits with the rim, above the shadow, and never writes a silhouette pixel - so it
    // is out of the material mask exactly as the other two are.
    node.foam.texture = l.foamTexture ?? Texture.EMPTY;
    node.foam.position.set(sx, sy);
    node.foam.visible = !maskMode && l.foamTexture !== null;
  }

  /** Replace a GUI sprite's texture with a fresh one, or hide it. Returns the texture to keep. */
  function retexture(sprite: Sprite, old: Texture | null, img: RgbaImage | null, x: number, y: number): Texture | null {
    if (old) old.destroy(true);
    if (!img) {
      sprite.visible = false;
      return null;
    }
    const t = textureFromRgba(img.w, img.h, img.rgba);
    sprite.texture = t;
    sprite.position.set(Math.round(x), Math.round(y));
    sprite.visible = true;
    return t;
  }

  /**
   * THE TONE SEA, one draw. Pure in (scene, tick, camera, islets, waterFrame): no counter, no
   * seed, no accrued state, so the same instant draws the same sea whether the tab is watched or
   * away. The scratch arrays below are RECOMPUTED EVERY DRAW - they exist so a corner sample is
   * taken once instead of four times, not so anything survives between draws.
   *
   *   the tone field   frozen in world space. No drift term: a drifting quantised field flips
   *                    large contiguous regions by one index every 190 ms, which is a
   *                    low-frequency area flicker and is the failure 1.5i(d) already ruled on.
   *   the runs         cells of equal band coalesce along a row and draw as ONE tinted white
   *                    sprite. An 8-bit multiply of white by a tint is the tint exactly.
   *   the boundary     one hard-alpha marching-squares overlay per cell PER BAND LEVEL it spans,
   *                    in increasing order, so a three-band cell is drawn correctly and no two
   *                    neighbours ever disagree about which threshold they are drawing.
   */
  function drawToneSea(scene: CalmScene, tick: number, ox: number, oy: number, islets: readonly PlacedIslet[], waterFrame: number): void {
    probe0 = performance.now();
    toneRuns = 0;
    edgeCells = 0;
    crestCells = 0;
    mixedCells = 0;
    for (const b of toneBuckets) b.used = 0;
    for (const b of edgeBuckets) b.used = 0;
    for (const b of crestBuckets) b.used = 0;
    const S = SEA_CELL_PX;
    const N = seaParams.tones;
    // The lattice that covers the frame, anchored in WORLD space so it scrolls under the camera.
    // Screen x = world x + ox, so the frame shows world x in [-ox, ART_W - ox]. Written once as
    // `ART_W + ox` this was a NEGATIVE cell count everywhere the camera was far from the world
    // origin - and the sea simply did not draw. It only looked right at Murrin Roost, which is
    // near (0, 0), and the two captures that caught it were Torsk Vard and the open sea.
    const i0 = Math.floor(-ox / S);
    const j0 = Math.floor(-oy / S);
    const cw = Math.ceil((viewW - ox) / S) - i0;
    const ch = Math.ceil((viewH - oy) / S) - j0;
    const gw = cw + 1;
    const gh = ch + 1;
    const n = gw * gh;
    if (toneT_.length < n) {
      fieldCacheKey = '';
      toneT_ = new Float32Array(n);
      bandG = new Int16Array(n);
      warpX = new Float32Array(n);
      warpY = new Float32Array(n);
      ampG = new Float32Array(n);
    }

    // THE SHORES THAT HAVE A SHELF. Only sprites on screen (the shore set is already culled) and
    // only on this arm; the boat has none.
    const shelves: { f: ShelfField; x: number; y: number }[] = [];
    let shoreSig = '';
    for (const p of islets) {
      const l = loaded.get(p.sprite);
      if (!l || l.shelf === null) continue;
      const ax = p.x - l.anchor.x;
      const ay = p.y - l.anchor.y;
      // CULLED ON THE FIELD'S OWN REACH, not on the box (water.ts `shelfCullMarginPx`): a bare
      // box test truncates a 96 px shelf 96 px early and steps the tone band by up to 5 of 7 on
      // the one draw a sprite's box crosses the frame edge.
      if (!boxWithinFrame(ax + ox, ay + oy, l.w, l.h, viewW, viewH, shelfMargin)) continue;
      shelves.push({ f: l.shelf, x: ax, y: ay });
      shoreSig += `${p.sprite}@${ax},${ay};`;
    }

    /**
     * THE FROZEN HALF OF THE FIELD, MEMOISED - and it is a memo of a PURE FUNCTION keyed on
     * EVERY ONE of its inputs, not a cache of state.
     *
     * The tone field has no time axis at all (that is this round's "no drift term"), so
     * `warpX`, `warpY`, `toneT_` and `bandG` depend on exactly four things: which lattice cell
     * the frame starts at, how big the lattice is, the sea's constants, and which sprites'
     * shelves are on screen. The camera moves one to three art px a draw and the lattice is 16,
     * so those four are unchanged on most consecutive draws.
     *
     * WHY THIS IS NOT THE STATE THE PRESENCE LAW FORBIDS. The key contains every input that
     * moves, so a hit returns exactly what a miss would compute; nothing accrues, nothing
     * drifts, and a tab that has drawn a thousand times holds the same arrays as one that has
     * drawn once. The fence is a photograph rather than an argument: `sea-shots.mjs` freezes two
     * pages at the identical instant, one having drawn many more times than the other, and
     * asserts the two captures are SHA-256 identical - which is exactly what a wrong key fails.
     *
     * MEASURED, browser, grain 1, before the memo: the frozen half was 0.68 ms of a 1.98 ms
     * draw and the crest another 0.86.
     */
    const fieldKey = `${i0},${j0},${gw},${gh}|${shoreSig}`;
    if (fieldKey !== fieldCacheKey) {
      fieldCacheKey = fieldKey;
      for (let j = 0; j < gh; j++) {
        const wy = (j0 + j) * S;
        for (let i = 0; i < gw; i++) {
          const wx = (i0 + i) * S;
          const q = j * gw + i;
          // THE SHELF IS LOOKED UP AT THE WARPED POINT. See `toneTWarped`: at the true point the
          // shelf's iso-lines stay smooth and the bay draws as a bathymetric map.
          warpInto(wx, wy, seaParams, warpPair, 0);
          const px = warpPair[0]!;
          const py = warpPair[1]!;
          warpX[q] = px;
          warpY[q] = py;
          let d = Number.POSITIVE_INFINITY;
          for (const s of shelves) {
            const k = shelfDistAt(s.f, px - s.x, py - s.y);
            if (k < d) d = k;
          }
          const t = toneTWarped(px, py, d, seaParams);
          toneT_[q] = t;
          bandG[q] = bandOf(t, N);
        }
      }
      fieldMisses++;
    }
    // THE LIVE HALF, every draw: the calm amplitude follows the BOAT, which moves.
    for (let j = 0; j < gh; j++) {
      const wy = (j0 + j) * S;
      for (let i = 0; i < gw; i++) ampG[j * gw + i] = calmAmplitude((i0 + i) * S, wy, tick, scene, calm);
    }

    probeA = performance.now();
    // ---- the runs, and the boundary overlays over them ---------------------------------------
    for (let j = 0; j < ch; j++) {
      const sy = (j0 + j) * S + oy;
      let runStart = 0;
      let runBand = -1;
      const flush = (endExclusive: number): void => {
        if (runBand < 0 || endExclusive <= runStart) return;
        const s = takeFrom(toneBuckets[runBand]!, toneHex[runBand]!, null);
        s.position.set((i0 + runStart) * S + ox, sy);
        s.width = (endExclusive - runStart) * S;
        s.height = S;
        toneRuns++;
      };
      for (let i = 0; i < cw; i++) {
        const a = j * gw + i;
        const b0 = bandG[a]!;
        const b1 = bandG[a + 1]!;
        const b2 = bandG[a + gw + 1]!;
        const b3 = bandG[a + gw]!;
        const lo = Math.min(b0, b1, b2, b3);
        const hi = Math.max(b0, b1, b2, b3);
        if (hi > lo + 1) mixedCells++;
        if (lo !== runBand) {
          flush(i);
          runStart = i;
          runBand = lo;
        }
        if (hi === lo) continue;
        const t0 = toneT_[a]!;
        const t1 = toneT_[a + 1]!;
        const t2 = toneT_[a + gw + 1]!;
        const t3 = toneT_[a + gw]!;
        for (let m = lo; m < hi; m++) {
          const thr = bandThreshold(m, N);
          const c = marchCase(t0, t1, t2, t3, thr);
          cellEdgeCrossings(t0, t1, t2, t3, thr, cross);
          const idx = bankIndexOf(c, cross[0]!, cross[1]!, cross[2]!, cross[3]!);
          if (idx < 0) continue;
          const band = Math.min(N - 1, m + 1);
          const s = takeFrom(edgeBuckets[band]!, toneHex[band]!, edgeBank[idx]!);
          s.position.set((i0 + i) * S + ox, sy);
          edgeCells++;
        }
      }
      flush(cw);
    }

    probeB = performance.now();
    // ---- THE CREST: a second marching-squares band, on its own finer lattice -----------------
    //
    // The field marched on is `crestField - crestThreshold`, at zero. Both halves are per-corner,
    // so a crossing is a function of the two corners two neighbouring cells share and the seam
    // guarantee is the same one the tone boundary has.
    //
    // CALM MOVES COVERAGE. The threshold is 1 - crestGain * calmAmplitude * 0.35, so where the
    // water is quiet the contour retreats to the field's few remaining peaks and where it is
    // alive the network fills in. The LIFT moves with the same scalar (palette.ts `crestFor`,
    // 20 + 30a luma) - if only the coverage moved, a shoreline could flash at a fixed +45 with
    // the whole calm suite green, which is risk 2 of this round.
    //
    // The warp and the amplitude are sampled on the TONE lattice and interpolated to this one.
    // Both are smooth over hundreds of px against this lattice's 8, and it is the difference
    // between 2,135 calmAmplitude calls a draw - which is parity with the settled arm's 2,160 -
    // and 8,349 of them.
    const C = SEA_CREST_CELL_PX;
    const per = S / C;
    const ci0 = i0 * per;
    const cj0 = j0 * per;
    const ccw = cw * per;
    const cch = ch * per;
    const cgw = ccw + 1;
    const cgh = cch + 1;
    if (crestG.length < cgw * cgh) {
      crestG = new Float32Array(cgw * cgh);
      crestBase = new Float32Array(cgw * cgh);
      crestNeed = new Uint8Array(cgw * cgh);
      crestDash = new Uint8Array(cgw * cgh);
      crestPX = new Float32Array(cgw * cgh);
      crestPY = new Float32Array(cgw * cgh);
      crestD = new Float32Array(cgw * cgh);
      crestNX = new Float32Array(cgw * cgh);
      crestNY = new Float32Array(cgw * cgh);
      crestSh = new Float32Array(cgw * cgh);
      crestLatticeKey = '';
      crestCacheFrame = Number.NaN;
    }
    // THE CREST HAS TWO MEMOS, NOT ONE, AND THAT IS WHERE ITS COST WENT.
    //
    // The dash gate and the warped sample positions are FROZEN IN WORLD SPACE - neither has a
    // time axis at all. The crest FIELD does: its whole motion is `STREAK_SWING[waterFrame]`, an
    // integer offset on the sample position. The first version keyed all three on the wave frame
    // together, so every 190 ms it recomputed 8,540 dash-gate hashes and 7,300 bilinear warp
    // interpolations that could not have changed.
    //
    // MEASURED, `tools/instruments/water-probe.mjs` at Torsk Vard before the split: the crest was
    // 2.4 ms of a 5.6 ms draw and its memo missed on 31 of 38 draws, which is once per wave
    // frame - i.e. always. `water-cost.mjs` read mainThreadMsPerSecond 2.016x the settled arm
    // there over five rounds (2.003, 2.016, 2.067, 2.003, 2.043), against a bar of 2.0. That is
    // not noise and it was not something to report around.
    //
    // The two keys are exactly the two questions: has the LATTICE moved (fieldKey), and has the
    // FRAME moved. Both are memos of pure functions keyed on every input, which is the same thing
    // the tone field's memo is and not the state the presence law forbids.
    if (crestLatticeKey !== fieldKey) {
      crestLatticeKey = fieldKey;
      crestCacheFrame = Number.NaN;
      crestNeed.fill(0, 0, cgw * cgh);
      for (let j = 0; j < cch; j++) {
        for (let i = 0; i < ccw; i++) {
          const wx = (ci0 + i) * C;
          const wy = (cj0 + j) * C;
          // THE SEA IS NOT COMPUTED WHERE THE SEA CANNOT BE SEEN (shelf.ts `solid`). A crest cell
          // whose whole 8 x 8 square is under solid silhouette is drawn and then painted over by
          // the sprite; at Torsk Vard that is a third of the frame, and the crest's per-corner
          // field is the most expensive thing in the draw. `shelfSolidSquare` rounds OUTWARD, so
          // a cell touching one block that is not wholly opaque is kept. The captures before and
          // after this are SHA-256 identical, which is the only acceptable proof for it.
          let hidden = false;
          // THE CELL'S OWN SHORENESS, for the dash gate alone (sea.ts `crestDashOnAt`): the gate
          // is a property of the CELL and the field is a property of its CORNERS, so this probe
          // is the cell's and the four below are the corners'. One extra lattice lookup per cell,
          // in the memo that only runs when the lattice moves.
          let cellD = Number.POSITIVE_INFINITY;
          let cellN = 0;
          for (const sh of shelves) {
            if (shelfSolidSquare(sh.f, wx - sh.x, wy - sh.y, C)) { hidden = true; break; }
            const k = shelfProbeInto(sh.f, wx - sh.x, wy - sh.y, shelfProbe, 0);
            if (k < cellD) {
              cellD = k;
              cellN = Math.abs(shelfProbe[1]!) + Math.abs(shelfProbe[2]!);
            }
          }
          const on = !hidden && crestDashAt(wx, wy, crestDashOnAt(cellN === 0 ? 0 : shorenessAt(cellD, seaParams), seaParams)) ? 1 : 0;
          crestDash[j * cgw + i] = on;
          if (!on) continue;
          const a = j * cgw + i;
          crestNeed[a] = 1;
          crestNeed[a + 1] = 1;
          crestNeed[a + cgw] = 1;
          crestNeed[a + cgw + 1] = 1;
        }
      }
      for (let j = 0; j <= cch; j++) {
        const fy = j / per;
        const jj = Math.min(gh - 2, Math.floor(fy));
        const ty = fy - jj;
        const ty1 = 1 - ty;
        for (let i = 0; i <= ccw; i++) {
          const q = j * cgw + i;
          if (!crestNeed[q]) continue;
          const fx = i / per;
          const ii = Math.min(gw - 2, Math.floor(fx));
          const tx = fx - ii;
          const tx1 = 1 - tx;
          const a = jj * gw + ii;
          const b = a + gw;
          const px = warpX[a]! * tx1 * ty1 + warpX[a + 1]! * tx * ty1 + warpX[b]! * tx1 * ty + warpX[b + 1]! * tx * ty;
          const py = warpY[a]! * tx1 * ty1 + warpY[a + 1]! * tx * ty1 + warpY[b]! * tx1 * ty + warpY[b + 1]! * tx * ty;
          crestPX[q] = px;
          crestPY[q] = py;
          // THE SHORE, AT THE TRUE POINT - AND THAT IS THE OPPOSITE OF WHAT THE TONE DOES.
          //
          // `toneTWarped`'s note is that the tone MUST look the shelf up at the warped point or
          // Murrin Roost's bay draws as a bathymetric map: five concentric hard bands parallel to
          // the shore. The crest is the other case, and MEASURED. The tone's shelf reaches 96 art
          // px and a 40 px warp is a fifth of it - an irregularity. The crest's band is 14 art px
          // across, and a 40 px warp is THREE BANDS - which does not decorate the across-shore
          // coordinate, it destroys it. MEASURED at Murrin Roost's shelf with the crest reading
          // the warped point: mean cos(2 dtheta) against the local shore tangent 0.3253 with the
          // shore band at full weight, against woolstrand-8302's own 0.8416. The marks were as
          // shore-parallel as the warp let them be, which was not very.
          //
          // So the crest's shore is read HERE, at the lattice corner's own world position, and
          // the warp keeps the open sea's field where it belongs. The distance and the normal
          // come from the SAME shelf - the nearest one - so a mark is never placed by one island
          // and turned by another.
          const wxT = (ci0 + i) * C;
          const wyT = (cj0 + j) * C;
          let d = Number.POSITIVE_INFINITY;
          let nx = 0;
          let ny = 0;
          for (const s of shelves) {
            const k = shelfProbeInto(s.f, wxT - s.x, wyT - s.y, shelfProbe, 0);
            if (k < d) {
              d = k;
              nx = shelfProbe[1]!;
              ny = shelfProbe[2]!;
            }
          }
          crestD[q] = d;
          crestNX[q] = nx;
          crestNY[q] = ny;
          crestSh[q] = nx === 0 && ny === 0 ? 0 : shorenessAt(d, seaParams);
        }
      }
      latticeMisses++;
    }
    // KEYED ON THE OFFSET, NOT THE FRAME (sea.ts `crestFrameKey`): sixteen frames carry nine
    // distinct STREAK_SWING offsets, and the crest field depends on the frame only through that
    // offset, so this misses nine times a loop instead of sixteen.
    const crestKey = crestFrameKey(waterFrame);
    if (crestCacheFrame !== crestKey) {
      crestCacheFrame = crestKey;
      // Walked as (j, i) and not as one flat index, because the shore band is read in the
      // corner's TRUE world position - see the lattice memo above on why the warped one cannot
      // carry a 14 px band.
      for (let j = 0; j <= cch; j++) {
        for (let i = 0; i <= ccw; i++) {
          const q = j * cgw + i;
          if (!crestNeed[q]) continue;
          crestBase[q] = crestFieldAt(crestPX[q]!, crestPY[q]!, (ci0 + i) * C, (cj0 + j) * C, crestD[q]!, crestNX[q]!, crestNY[q]!, crestSh[q]!, waterFrame, seaParams);
        }
      }
      crestMisses++;
    }
    for (let j = 0; j <= cch; j++) {
      const fy = j / per;
      const jj = Math.min(gh - 2, Math.floor(fy));
      const ty = fy - jj;
      const ty1 = 1 - ty;
      for (let i = 0; i <= ccw; i++) {
        const c0 = j * cgw + i;
        if (!crestNeed[c0]) continue;
        const fx = i / per;
        const ii = Math.min(gw - 2, Math.floor(fx));
        const tx = fx - ii;
        const tx1 = 1 - tx;
        const a = jj * gw + ii;
        const b = a + gw;
        const amp = ampG[a]! * tx1 * ty1 + ampG[a + 1]! * tx * ty1 + ampG[b]! * tx1 * ty + ampG[b + 1]! * tx * ty;
        // THE DENSITY ANSWERS THE LAND (CONTRACT 1.5y(1)): the calm's own coverage, thinned by
        // `crestOpen` where there is no shore in reach. `crestSh` is frozen with the lattice, so
        // this costs one array read a corner over what the calm alone asked for.
        const coverage = crestCoverageAt(amp, crestSh[c0]!, seaParams);
        crestG[c0] = crestBase[c0]! - (1 - coverage);
      }
    }
    for (let j = 0; j < cch; j++) {
      const sy = (cj0 + j) * C + oy;
      for (let i = 0; i < ccw; i++) {
        if (!crestDash[j * cgw + i]) continue;
        const a = j * cgw + i;
        const g0 = crestG[a]!;
        const g1 = crestG[a + 1]!;
        const g2 = crestG[a + cgw + 1]!;
        const g3 = crestG[a + cgw]!;
        const c = marchCase(g0, g1, g2, g3, 0);
        if (c === 0 || c === 15) continue;
        cellEdgeCrossings(g0, g1, g2, g3, 0, cross);
        const idx = bankIndexOf(c, cross[0]!, cross[1]!, cross[2]!, cross[3]!);
        if (idx < 0) continue;
        // The tone and the amplitude the crest sits on: the tone lattice cell this crest cell
        // falls in, so the crest is a lift of the water it is drawn over and never a value from
        // some other part of the ramp.
        const ti = Math.min(gw - 1, Math.floor(i / per));
        const tj = Math.min(gh - 1, Math.floor(j / per));
        const tq = tj * gw + ti;
        const k = bandG[tq]! * CREST_LIFT_STEPS + Math.round(Math.min(1, Math.max(0, ampG[tq]!)) * (CREST_LIFT_STEPS - 1));
        const s = takeFrom(crestBuckets[k]!, crestHex[k]!, crestBank[idx]!);
        s.position.set((ci0 + i) * C + ox, sy);
        crestCells++;
      }
    }

    probeC = performance.now();
    for (const b of [...toneBuckets, ...edgeBuckets, ...crestBuckets]) {
      for (let i = b.used; i < b.pool.length; i++) b.pool[i]!.visible = false;
    }
    probeD = performance.now();
  }

  function render(frame: DrawFrame): void {
    const t0 = performance.now();
    // One comparison, and it stops being taken after the first frame of the view's life.
    if (spritesAtFirstDraw < 0) spritesAtFirstDraw = spritesLoaded;
    drawSerial++;
    const cam = frame.camPx;
    const ox = halfW - cam.x;
    const oy = halfH - cam.y;
    // The boat's own centre in SCREEN art px. `ART_W / 2, ART_H / 2` while the camera is on the
    // boat, which is every frame in open water, and off it by the arrival shift alongside a place.
    const boatCx = frame.boatPx.x + ox;
    const boatCy = frame.boatPx.y + oy;

    // OPEN WATER, tiled in WORLD space so it scrolls under the camera as the boat sails. Off
    // in the mask: a pixel is water if nothing covered it, and a crest covers nothing.
    //
    // THE FIELD (CONTRACT 1.5i(d)). Each cell draws its slice of the tile at the amplitude
    // level water.ts gives that cell's centre for this wave frame's tick: 1 at the boat (which
    // is NO LONGER the camera - see `DrawFrame.boatPx`) and at every shoreline ON SCREEN, the
    // calm floor plus gusts far from both. The hold decision is per TILE so a tile stays
    // seamless with itself.
    //
    // A SHORELINE IS THE STADIUM of the sprite's own box (water.ts `Shore`), not the disc of
    // its mean half-extent this comment used to name - the disc was round 4's and CONTRACT
    // 1.5h(b)'s large islands are what retired it.
    //
    // AND THE SHORE SET IS CULLED TO THE FRAME, GROWN BY THE FIELD'S OWN REACH. The host asks
    // for islets ISLET_MARGIN_LEAGUES (6 x, 8 y = 240 x 277 art px) beyond the frame edge so
    // nothing pops in, and every one of them was becoming a shore, so a sprite wholly off
    // screen lifted the water at the edge it was behind - that is what WP0 was about.
    //
    // WP0 SHIPPED THE BARE BOX TEST AND THAT WAS A DEFECT OF ITS OWN. A shore's lift does not
    // stop at its box; it stops `shoreRadius` past it. MEASURED by a critic on the draw a
    // sprite's box crosses the edge: amplitude 0.285 -> 1.000, level 9 -> 31 of 32, over an
    // ~80 px band, in ONE draw, on BOTH arms - the flip class 1.5i(d) ruled on. The test is
    // now the box grown by SHORE_CULL_MARGIN_PX (water.ts), which is the distance at which the
    // lift is exactly zero, so what is dropped provably could not have drawn anything.
    // `arrival.mjs` - the instrument that gates 1.5q(b) - imports the same constant and the
    // same predicate, so the model and the renderer cannot drift apart again.
    waterLayer.visible = !maskMode;
    waterTiles = 0;
    waterCells = 0;
    let crossCells = 0;
    if (!maskMode) {
      const shores: Shore[] = [];
      for (const p of frame.islets) {
        const l = loaded.get(p.sprite);
        if (!l) continue;
        const ax = p.x - l.anchor.x + ox;
        const ay = p.y - l.anchor.y + oy;
        if (!boxWithinFrame(ax, ay, l.w, l.h, viewW, viewH, SHORE_CULL_MARGIN_PX)) continue;
        shores.push({ x: p.x - l.anchor.x + l.w / 2, y: p.y - l.anchor.y + l.h / 2, hw: l.w / 2, hh: l.h / 2 });
      }
      const scene: CalmScene = { boat: frame.boatPx, shores };
      const tick = frame.waterTickMs;
      // THE ONE BRANCH (CONTRACT 1.5w, `?water=`). Everything above is shared - the shore set
      // and the scene are the same two arms of the same field - and everything below it is one
      // arm or the other. The settled arm's loop is byte-identical to what it was.
      if (waterMode === 'render') {
        drawToneSea(scene, tick, ox, oy, frame.islets, frame.waterFrame);
        for (const s of waterPool) s.visible = false;
        for (const c of crossPool) c.visible = false;
      } else {
      const [tx0, tx1] = tileRange(cam.x - halfW, cam.x + viewW - halfW);
      const [ty0, ty1] = tileRange(cam.y - halfH, cam.y + viewH - halfH);
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          const wx0 = tx * WATER_TILE_PX;
          const wy0 = ty * WATER_TILE_PX;
          let f = tileFrame(tx, ty, frame.waterFrame, waterField);
          if (calm.hold > 0 && calmAmplitude(wx0 + WATER_TILE_PX / 2, wy0 + WATER_TILE_PX / 2, tick, scene, calm) < calm.hold) f = heldFrame(f);
          const f2 = (f + WATER_LOOP_FRAMES / 2) % WATER_LOOP_FRAMES;
          for (let cy = 0; cy < CELLS; cy++) {
            for (let cx = 0; cx < CELLS; cx++) {
              const wx = wx0 + cx * WATER_CELL_PX;
              const wy = wy0 + cy * WATER_CELL_PX;
              const level = levelOf(calmAmplitude(wx + WATER_CELL_PX / 2, wy + WATER_CELL_PX / 2, tick, scene, calm));
              let s = waterPool[waterCells];
              if (!s) {
                s = new Sprite();
                waterPool.push(s);
                waterLayer.addChildAt(s, waterCells);
              }
              s.texture = waterLevels[level]![f]![cy * CELLS + cx]!;
              s.position.set(wx + ox, wy + oy);
              s.visible = true;
              waterCells++;
              if (waterField === 'cross') {
                let c = crossPool[crossCells];
                if (!c) {
                  c = new Sprite();
                  crossPool.push(c);
                  crossLayer.addChild(c);
                }
                c.texture = waterLevels[level]![f2]![cy * CELLS + cx]!;
                c.position.set(wx + ox, wy + oy);
                c.alpha = crossWeightAt(wx + WATER_CELL_PX / 2, wy + WATER_CELL_PX / 2, tick, calm);
                c.visible = true;
                crossCells++;
              }
            }
          }
          waterTiles++;
        }
      }
      for (let i = waterCells; i < waterPool.length; i++) waterPool[i]!.visible = false;
      for (let i = crossCells; i < crossPool.length; i++) crossPool[i]!.visible = false;
      }
    }

    // EVERYTHING ON THE WATER, ISLANDS AND BOAT TOGETHER, nearest the camera last. The boat is an
    // item in this list rather than an append after it; see `paintOrder`.
    const visible: { id: string; node: ContactNode; loaded: LoadedSprite; sx: number; sy: number; feetPx: number }[] = [];
    for (const p of frame.islets) {
      const l = loaded.get(p.sprite);
      if (!l) continue;
      const sx = Math.round(p.x - l.anchor.x + ox);
      const sy = Math.round(p.y - l.anchor.y + oy);
      if (sx + l.w <= 0 || sy + l.h + BAND_PX <= 0 || sx >= viewW || sy >= viewH) continue;
      let node = islands.get(p.id);
      if (!node) {
        node = { ...contactNode(), loaded: l, lastSeen: drawSerial };
        islands.set(p.id, node);
      }
      node.lastSeen = drawSerial;
      visible.push({ id: p.id, node, loaded: l, sx, sy, feetPx: sy + l.footPx });
    }
    const bl = boatFor(frame.boatHeadingDeg);
    const bsx = Math.round(boatCx - bl.w / 2);
    const bsy = Math.round(boatCy - bl.h / 2);
    visible.push({ id: 'boat', node: boatNode, loaded: bl, sx: bsx, sy: bsy, feetPx: bsy + bl.footPx });

    bandsLayer.removeChildren();
    spritesLayer.removeChildren();
    for (const v of paintOrder(visible)) {
      place(v.node, v.loaded, v.sx, v.sy);
      bandsLayer.addChild(v.node.shadow, v.node.foam, v.node.rim);
      spritesLayer.addChild(v.node.img);
    }
    for (const [id, node] of islands) {
      if (node.lastSeen !== drawSerial) {
        node.img.destroy();
        node.rim.destroy();
        node.shadow.destroy();
        node.foam.destroy();
        islands.delete(id);
      }
    }

    // The catch mark rides the BOAT, not the frame. Re-applied every draw so a camera swinging
    // onto an island carries it along instead of leaving it out on the water.
    if (catchSprite.visible) catchSprite.position.set(Math.round(boatCx + catchOffset.dx), Math.round(boatCy + catchOffset.dy));
    // So does the line: it left the hull, and the hull is where the boat is drawn.
    if (fishingSprite.visible) fishingSprite.position.set(Math.round(boatCx + fishingOffset.dx), Math.round(boatCy + fishingOffset.dy));

    guiLayer.visible = guiVisible && !maskMode;
    // The line goes with the paper: off in the mask and out of the instruments' capture.
    lineLayer.visible = guiVisible && !maskMode;

    const clearColor = maskMode ? rgbHex(MASK_WATER_RGB) : rgbHex(base);
    app.renderer.render({ container: world, target: art, clearColor });
    app.renderer.render({ container: screen, clearColor: 0x000000 });
    draws++;
    lastDrawMs = performance.now() - t0;
  }

  /**
   * THE ISLANDS ARRIVING, and the one redraw each of them is allowed to cost.
   *
   * The host's step loop only draws when its frame KEY changes (`world-entry.ts`), which is
   * how this game idles at nothing. A sprite that lands while the boat is holding still would
   * therefore not appear until something else moved the camera, so the view redraws its own
   * last frame - the same thing `setGrain`, `setMaskMode` and `setGuiVisible` already do.
   *
   * THIS CANNOT BECOME A FRAME LOOP. It runs at most once per sprite for the life of the
   * view: eighteen times today, then never again. Arrivals that settle in the same task
   * coalesce into ONE redraw, because the microtask queued by the first runs after every
   * continuation already sitting behind it. There is no timer, no ticker and no polling here,
   * and nothing is scheduled once the last sprite is in.
   */
  for (const a of arriving) {
    void a.load.then((r) => {
      if (!r.ok) {
        // Not thrown: see the note where these are issued. The sea, the boat and every other
        // island still draw, and this says which one is missing and why.
        console.error(`world-view: ${a.spec.file} did not load; ${a.spec.id} will not be drawn`, r.err);
        return;
      }
      // The shelf and the foam are built HERE, once per island, and only on the arm that draws
      // them. The foam's key is a hash of the island's own id, so its fringe is its own and is
      // the same on every reload; `foamPx` is the width the round's design asked for, clamped by
      // contact.ts to the band's own depth (see FoamSpec).
      const foam: FoamSpec | null = waterMode === 'render'
        ? {
            key: hash32(a.spec.id.length, a.spec.id.charCodeAt(0) | 0, a.spec.id.charCodeAt(a.spec.id.length - 1) | 0, 0xf0a),
            widthPx: [seaParams.foamPx, Math.round(seaParams.foamPx * 0.6), 0],
            rgb: [foamFor(base, 0), foamFor(base, 1), foamFor(base, 2)],
            rgba: r.img.rgba,
          }
        : null;
      const built = buildLoaded(a.spec.id, r.img, a.spec.anchor, rim, r.img, waterMode === 'render' ? seaParams.shelfPx : null, foam);
      for (let i = 0; i < 3; i++) foamColumns[i] = foamColumns[i]! + built.foamColumns[i]!;
      foamPxTotal += built.foamPx;
      loaded.set(a.spec.id, built);
      spritesLoaded++;
      // The last one in latches how many frames the player had already been shown. Under the
      // `await` this function refuses to do, every handler runs before anything is drawn and
      // this latches 0 - which is the whole of what that regression looks like from outside.
      if (spritesLoaded === arriving.length) drawsBeforeLastArrival = draws;
      if (lastFrame === null || arrivalRedrawQueued) return;
      arrivalRedrawQueued = true;
      queueMicrotask(() => {
        arrivalRedrawQueued = false;
        if (lastFrame === null) return;
        arrivalRedraws++;
        render(lastFrame);
      });
    });
  }

  return {
    canvas,
    manifest,
    authored,
    get grain() {
      return grain;
    },
    get maskMode() {
      return maskMode;
    },
    get view() {
      return { w: viewW, h: viewH };
    },
    setGrain(next: number, dpr: number, nextView?: ArtView, canvasDevice?: ArtView): void {
      const d = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
      const v = wholeView(nextView ?? { w: viewW, h: viewH });
      const cw = canvasDevice?.w ?? v.w * next;
      const ch = canvasDevice?.h ?? v.h * next;
      if (wholeGrain(next) === grain && v.w === viewW && v.h === viewH && canvas.width === cw && canvas.height === ch && canvas.style.width === `${canvas.width / d}px` && canvas.style.height === `${canvas.height / d}px`) return;
      sizeCanvas(next, d, v, canvasDevice);
      if (lastFrame) render(lastFrame);
    },
    draw(frame: DrawFrame): void {
      lastFrame = frame;
      render(frame);
    },
    async setBoatFittings(ids: readonly string[]): Promise<void> {
      const key = ids.join('|');
      if (key === fittingKey) return;
      fittingKey = key;
      const request = ++fittingRequest;
      try {
        const fittings: RgbaImage[] = [];
        for (const id of ids) {
          const url = cosmeticAssetUrls[`./baked/cosmetics/${id}.png`];
          if (!url) throw new Error(`Boat fitting art is missing: ${id}`);
          let loading = fittingLoads.get(id);
          if (!loading) { loading = loadRgba(url); fittingLoads.set(id, loading); }
          fittings.push(await loading);
          if (request !== fittingRequest) return;
        }
        if (request !== fittingRequest) return;
        boatAppearance = composeBoatAppearance(boatImage, fittings);
        if (boat) { destroyLoaded(boat.loaded); boat = null; }
        // Equipping while moored must redraw even when the clock/heading key did not change.
        if (lastFrame) render(lastFrame);
      } catch (error) {
        if (request !== fittingRequest) return;
        // Appearance still belongs to the last successful load. An invalid key ensures
        // unequipping after failure does not mistake that old appearance for the bare hull.
        fittingKey = null;
        for (const id of ids) fittingLoads.delete(id);
        throw error;
      }
    },
    setMaskMode(on: boolean): void {
      if (on === maskMode) return;
      maskMode = on;
      if (lastFrame) render(lastFrame);
    },
    setChart(paper: RgbaImage | null, cast: RgbaImage | null, y: number, x = 0): void {
      chartTexture = retexture(chartSprite, chartTexture, paper, x, y);
      chartCastTexture = retexture(chartCastSprite, chartCastTexture, cast, x, y);
    },
    setCatchMark(img: RgbaImage | null, dx: number, dy: number): void {
      catchOffset.dx = dx;
      catchOffset.dy = dy;
      // Position is applied in `render`, off the boat, so this does not redraw: the host paints
      // immediately after, the same way it does for `setChart`.
      catchTexture = retexture(catchSprite, catchTexture, img, 0, 0);
    },
    setFishingVisual(model: FishingVisual | null): void {
      // Repainted only when a pixel of it would differ: the model is small enough to be its own key.
      const key = model === null ? '' : JSON.stringify(model);
      if (key === fishingKey) return;
      fishingKey = key;
      const painted = model === null ? null : paintFishing(model);
      fishingOffset.dx = painted?.dx ?? 0;
      fishingOffset.dy = painted?.dy ?? 0;
      fishingTexture = retexture(fishingSprite, fishingTexture, painted?.img ?? null, 0, 0);
    },
    setGuiVisible(on: boolean): void {
      if (on === guiVisible) return;
      guiVisible = on;
      if (lastFrame) render(lastFrame);
    },
    async capture(): Promise<{ frame: string; mask: string; key: string }> {
      if (!lastFrame) throw new Error('nothing drawn yet');
      const was = maskMode;
      // The instruments judge the world, not the interface. A 12% strip of paper across the
      // bottom would move every water fraction and every mask figure they compute, so the
      // chart is off for the whole capture and back afterwards.
      const wasGui = guiVisible;
      guiVisible = false;
      maskMode = false;
      render(lastFrame);
      const frame = await app.renderer.extract.base64(art);
      maskMode = true;
      render(lastFrame);
      const mask = await app.renderer.extract.base64(art);
      maskMode = was;
      guiVisible = wasGui;
      render(lastFrame);
      return { frame, mask, key: lastFrame.key };
    },
    stats(): WorldViewStats {
      return {
        draws, boatRebuilds, lastDrawMs, grain, viewW, viewH, waterTiles, waterCells, waterField, sea, seaRgb: base, calm,
        waterMode, seaParams, toneRuns, edgeCells, crestCells, mixedCells, bankBytes: edgeBankBytes + crestBankBytes, fieldMisses, crestMisses, latticeMisses, probe: { field: +(probeA - probe0).toFixed(3), edges: +(probeB - probeA).toFixed(3), crest: +(probeC - probeB).toFixed(3), hide: +(probeD - probeC).toFixed(3) }, foamColumns: [...foamColumns] as [number, number, number], foamPx: foamPxTotal,
        spritesLoaded, spritesTotal: arriving.length, arrivalRedraws, spritesAtFirstDraw, drawsBeforeLastArrival,
      };
    },
  };
}
